import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, KeyRound, RotateCcw, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtDate, fmtTime, fmtErr } from "../api";

// SuperAdmin-only Employee Login Management.
// Data: GET /api/admin/login-management/overview (safe whitelisted fields only —
// the API never returns passwords, hashes, JWTs or secrets, and none are rendered here).
// Actions reuse existing deployed endpoints:
//   POST /api/users/{id}/onboarding-token  (generate / regenerate = "Reset Access")
//   PATCH /api/users/{id} {active}         (activate / deactivate)

const uid = (u) => u.id || u.user_id;
const loginId = (u) => u.username || u.login_id || "—";

const everLoggedIn = (u) => {
  if (u.has_ever_logged_in != null) return !!u.has_ever_logged_in;
  if (u.ever_logged_in != null) return !!u.ever_logged_in;
  return !!u.last_login;
};

const onboardingStatus = (u) => {
  const explicit = u.onboarding_status || u.invite_status;
  if (explicit) return String(explicit);
  if (everLoggedIn(u)) return "Completed";
  if (u.onboarding_used_at) return "Completed";
  const exp = u.onboarding_expires_at || u.onboarding_expiry;
  if (exp) return new Date(exp) < new Date() ? "Expired" : "Pending";
  if (u.onboarding_pending === true) return "Pending";
  return "Not Started";
};

const OB_STYLES = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  expired: "bg-red-50 text-[#C5221F] border-red-200",
};

const OnboardingBadge = ({ value }) => {
  const cls = OB_STYLES[(value || "").toLowerCase()] || "bg-slate-100 text-slate-500 border-slate-200";
  return <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>{value}</span>;
};

const LoginManagement = () => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [obFilter, setObFilter] = useState("");
  const [acting, setActing] = useState("");
  const [links, setLinks] = useState({}); // userId -> freshly generated onboarding link (session only)
  const [copied, setCopied] = useState("");

  const load = async () => {
    setError("");
    try {
      const { data } = await staffApi.get("/admin/login-management/overview");
      setRows(Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : Array.isArray(data?.users) ? data.users : []);
    } catch (e) {
      setError(fmtErr(e));
      setRows([]);
    }
  };

  useEffect(() => { load(); }, []);

  const roles = useMemo(() => [...new Set((rows || []).map((u) => u.role).filter(Boolean))].sort(), [rows]);

  const filtered = (rows || []).filter((u) => {
    if (roleFilter && u.role !== roleFilter) return false;
    if (statusFilter === "active" && u.active === false) return false;
    if (statusFilter === "inactive" && u.active !== false) return false;
    if (obFilter && onboardingStatus(u).toLowerCase() !== obFilter) return false;
    const s = `${u.name || u.employee_name} ${u.employee_code} ${loginId(u)} ${u.email} ${u.department}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  // Extract a copyable onboarding link from the (untyped) generate-token response.
  const extractLink = (d) => {
    if (!d) return "";
    if (typeof d === "string") return d;
    for (const k of ["onboarding_url", "onboarding_link", "invite_url", "invite_link", "url", "link", "token"]) {
      if (d[k]) return String(d[k]);
    }
    return "";
  };

  const generate = async (u, regenerate) => {
    const id = uid(u);
    if (regenerate && !window.confirm("This will invalidate the previous pending onboarding link.\n\nReset access for " + (u.name || u.employee_name) + "?")) return;
    setActing(id + (regenerate ? "-reset" : "-gen"));
    try {
      const { data } = await staffApi.post(`/users/${id}/onboarding-token`);
      const link = extractLink(data);
      if (link) setLinks((m) => ({ ...m, [id]: link }));
      toast.success(regenerate ? "Access reset — new onboarding link generated." : "Onboarding link generated.");
      if (!link) toast.message("The API did not return a copyable link.", { description: "Check the response shape with the backend team." });
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setActing("");
    }
  };

  const copy = async (u) => {
    const id = uid(u);
    try {
      await navigator.clipboard.writeText(links[id]);
      setCopied(id);
      setTimeout(() => setCopied(""), 2000);
      toast.success("Onboarding link copied.");
    } catch {
      toast.error("Copy failed — select and copy the link manually.");
    }
  };

  const toggleActive = async (u) => {
    const id = uid(u);
    const activating = u.active === false;
    if (!window.confirm(`${activating ? "Activate" : "Deactivate"} account for ${u.name || u.employee_name}?`)) return;
    setActing(id + "-toggle");
    try {
      await staffApi.patch(`/users/${id}`, { active: activating });
      toast.success(`${u.name || u.employee_name} ${activating ? "activated" : "deactivated"}.`);
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setActing("");
    }
  };

  const selectCls = "border border-slate-300 px-3 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]";

  return (
    <div data-testid="admin-login-management">
      <h1 className="font-display text-2xl font-bold text-slate-900">Login Management</h1>
      <p className="mt-1 text-sm text-slate-500">Sign-in access, onboarding links and account status for every employee. Access is issued only via secure, expiring onboarding links.</p>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Search name, code, username, email, department…" value={q} data-testid="lm-search"
            onChange={(e) => setQ(e.target.value)}
            className="w-full border border-slate-300 pl-9 pr-3 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]" />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="lm-filter-role" className={selectCls}>
          <option value="">All Roles</option>
          {roles.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="lm-filter-status" className={selectCls}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select value={obFilter} onChange={(e) => setObFilter(e.target.value)} data-testid="lm-filter-onboarding" className={selectCls}>
          <option value="">All Onboarding</option>
          <option value="not started">Not Started</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {rows === null ? (
        <p className="mt-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : error ? (
        <p className="mt-6 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="lm-error">{error}</p>
      ) : filtered.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500 bg-white border border-slate-200 p-6" data-testid="lm-empty">No employees match the current filters.</p>
      ) : (
        <div className="mt-5 bg-white border border-slate-200 overflow-x-auto" data-testid="lm-table">
          <table className="w-full text-sm min-w-[1080px]">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                {["Code", "Employee", "Username / Login ID", "Email", "Role", "Department", "Account", "Onboarding", "Last Login", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono-tech text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((u) => {
                const id = uid(u);
                const ob = onboardingStatus(u);
                // "Not Started" gets a plain generate; any existing onboarding state gets
                // "Reset Access" with the invalidation warning (per spec wording).
                const needsReset = ob.toLowerCase() !== "not started";
                return (
                  <tr key={id} data-testid={`lm-row-${id}`}>
                    <td className="px-4 py-3 text-slate-600">{u.employee_code || "—"}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{u.name || u.employee_name}</td>
                    <td className="px-4 py-3 text-slate-600" data-testid={`lm-username-${id}`}>{loginId(u)}</td>
                    <td className="px-4 py-3 text-slate-600">{u.email || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{u.role}</td>
                    <td className="px-4 py-3 text-slate-600">{u.department || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${u.active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`} data-testid={`lm-status-${id}`}>
                        {u.active !== false ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3"><OnboardingBadge value={ob} /></td>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap" data-testid={`lm-lastlogin-${id}`}>
                      {u.last_login ? `${fmtDate(u.last_login)} ${fmtTime(u.last_login)}` : "Never"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        {needsReset ? (
                          <button onClick={() => generate(u, true)} disabled={!!acting} data-testid={`lm-reset-${id}`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#C5221F] hover:underline disabled:opacity-50">
                            {acting === id + "-reset" ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />} Reset Access
                          </button>
                        ) : (
                          <button onClick={() => generate(u, false)} disabled={!!acting} data-testid={`lm-generate-${id}`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0A1128] hover:underline disabled:opacity-50">
                            {acting === id + "-gen" ? <Loader2 size={11} className="animate-spin" /> : <KeyRound size={11} />} Generate Onboarding Link
                          </button>
                        )}
                        {links[id] && (
                          <button onClick={() => copy(u)} data-testid={`lm-copy-${id}`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:underline">
                            {copied === id ? <Check size={11} /> : <Copy size={11} />} {copied === id ? "Copied" : "Copy Link"}
                          </button>
                        )}
                        <button onClick={() => toggleActive(u)} disabled={!!acting} data-testid={`lm-toggle-${id}`}
                          className="text-[11px] font-semibold text-slate-500 hover:underline disabled:opacity-50">
                          {acting === id + "-toggle" ? <Loader2 size={11} className="animate-spin" /> : null}
                          {u.active !== false ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                      {links[id] && (
                        <p className="mt-1.5 text-[10px] text-slate-400 break-all max-w-[260px]" data-testid={`lm-link-${id}`}>{links[id]}</p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LoginManagement;
